import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAbility } from '../permissions/AbilityContext';
import { useEffect, useState } from 'react';
import { axiosClient } from '../services/axiosClient';

/**
 * A component that protects routes by checking if the user is authenticated
 * and has the required permissions
 * If not authenticated, redirects to the landing page
 * If not authorized, redirects to the specified fallback route or shows an unauthorized message
 */
const ProtectedRoute = ({ children, requiredAction, requiredSubject, fallbackRoute, showUnauthorized = false, requireSubscriptionForPro = false }) => {
  const { user } = useAuth();
  const ability = useAbility();
  const location = useLocation();
  const [subChecked, setSubChecked] = useState(!requireSubscriptionForPro);
  const [needsRedirect, setNeedsRedirect] = useState(false);
 

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!requireSubscriptionForPro) { if (!cancelled) setSubChecked(true); return; }
      if (!user || user.role !== 'professional_responsable') { if (!cancelled) setSubChecked(true); return; }
      const path = location?.pathname || '';
      if (path.startsWith('/pro/subscribe')) { if (!cancelled) setSubChecked(true); return; }
      try {
        const res = await axiosClient.get('/subscriptions/my/');
        const hasActive = !!res?.data?.has_active;
        if (!cancelled) {
          setNeedsRedirect(!hasActive);
          setSubChecked(true);
        }
      } catch (e) {
        if (!cancelled) {
          setNeedsRedirect(true);
          setSubChecked(true);
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [requireSubscriptionForPro, user, location]);

  // If user is not authenticated, redirect to landing page
  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (requireSubscriptionForPro && !subChecked) {
    return null;
  }
  if (requireSubscriptionForPro && subChecked && needsRedirect) {
    return <Navigate to="/pro/subscribe" replace />;
  }
  
  // If permissions are specified, check if the user has the required permissions
  if (requiredAction && requiredSubject) {
    const hasPermission = ability.can(requiredAction, requiredSubject);
    
    // If user doesn't have permission
    if (!hasPermission) {
      // If a fallback route is specified, redirect to it
      if (fallbackRoute) {
        return <Navigate to={fallbackRoute} replace />;
      }
      
      // If showUnauthorized is true, show an unauthorized message
      if (showUnauthorized) {
        return (
          <div className="unauthorized-container">
            <h2>Unauthorized Access</h2>
            <p>You don't have permission to access this page.</p>
            <button onClick={() => window.history.back()}>Go Back</button>
          </div>
        );
      }
    }
  }

  // If user is authenticated and has the required permissions, render the children components
  return children;
};

export default ProtectedRoute;
